import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Button, TextField, IconButton, Chip, CircularProgress, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select,
  MenuItem, Switch, FormControlLabel, Alert, Snackbar, Tabs, Tab, Tooltip, Radio,
  RadioGroup, FormLabel, TablePagination,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Delete as DeleteIcon,
  Edit as EditIcon, LocationOn as LocationIcon, MyLocation as MyLocationIcon,
  People as PeopleIcon, Map as MapIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  fetchLocations, deleteLocation, createLocation, updateLocation, fetchCategories,
} from '../../store/slices/locationSlice';
import { AppDispatch, RootState } from '../../store/store';
import { CreateLocation, UpdateLocation, LocationDto } from '../../types';
import { toast } from 'react-toastify';

const ALL_ROWS_PAGE_SIZE = 100000;
const ROWS_PER_PAGE_OPTIONS = [10, 50, 100, { label: 'All', value: -1 }];
const MAP_UNAVAILABLE_MESSAGE = 'Map unavailable. Please check your internet connection.';

declare global {
  interface Window {
    google?: any;
  }
}

let googlePlacesLoader: Promise<void> | null = null;
const loadGooglePlaces = (apiKey: string): Promise<void> => {
  if (typeof window === 'undefined') return Promise.reject(new Error('Window is not available'));
  if (window.google?.maps?.places) return Promise.resolve();

  if (googlePlacesLoader) return googlePlacesLoader;

  googlePlacesLoader = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-google-places="true"]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Places script')));
      return;
    }

    const script = document.createElement('script');
    script.dataset.googlePlaces = 'true';
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Places script'));
    document.head.appendChild(script);
  });

  return googlePlacesLoader;
};

// Fix Leaflet default marker icon issue with bundlers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Component to handle map clicks
const MapClickHandler: React.FC<{ onMapClick: (lat: number, lng: number) => void }> = ({ onMapClick }) => {
  useMapEvents({
    click(e: L.LeafletMouseEvent) {
      onMapClick(parseFloat(e.latlng.lat.toFixed(6)), parseFloat(e.latlng.lng.toFixed(6)));
    },
  });
  return null;
};

// Component to recenter map when coordinates change externally
const MapRecenter: React.FC<{ lat: number; lng: number }> = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    if (lat !== 0 || lng !== 0) {
      map.setView([lat, lng], map.getZoom());
    }
  }, [lat, lng, map]);
  return null;
};

const emptyForm: CreateLocation = {
  locationName: '', address: '', city: '', state: '', country: '', pincode: '',
  latitude: 0, longitude: 0, radiusInMeters: 100, locationCategoryId: undefined, description: '',
};

const LocationList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { locations, categories, loading, locationTotalCount } = useSelector((state: RootState) => state.locations);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<LocationDto | null>(null);
  const [form, setForm] = useState<CreateLocation>(emptyForm);
  const [formIsActive, setFormIsActive] = useState(true);
  const [coordinateMethod, setCoordinateMethod] = useState<'manual' | 'gps' | 'map'>('manual');
  const [mapUnavailable, setMapUnavailable] = useState(false);
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const placesInputRef = useRef<HTMLInputElement | null>(null);
  const [placesQuery, setPlacesQuery] = useState('');
  const [placeSearchResults, setPlaceSearchResults] = useState<
    Array<{ display_name: string; lat: string; lon: string }>
  >([]);
  const [isPlaceSearching, setIsPlaceSearching] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [errors, setErrors] = useState<{
  radiusInMeters?: string;
    }>({});
  const isEmpty = (value: string) => !value.trim();
  const trimmedName = form.locationName.trim(); 
  useEffect(() => {
    dispatch(fetchCategories({}));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchLocations({
      searchTerm: searchTerm.replace(/^\s+/, '') || undefined,
      pageNumber: page + 1,
      pageSize: rowsPerPage === -1 ? ALL_ROWS_PAGE_SIZE : rowsPerPage,
    }));
  }, [dispatch, searchTerm, page, rowsPerPage]);

  useEffect(() => {
    if (!dialogOpen || coordinateMethod !== 'map') {
      setMapUnavailable(false);
      return;
    }

    setMapUnavailable(!navigator.onLine);

    const handleOffline = () => setMapUnavailable(true);
    const handleOnline = () => setMapUnavailable(false);

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [dialogOpen, coordinateMethod]);

  useEffect(() => {
    if (!dialogOpen || coordinateMethod !== 'map') return;
    if (!googleMapsApiKey) return;

    let cancelled = false;
    let listener: any = null;

    loadGooglePlaces(googleMapsApiKey)
      .then(() => {
        if (cancelled) return;
        if (!placesInputRef.current) return;
        if (!window.google?.maps?.places) return;

        const autocomplete = new window.google.maps.places.Autocomplete(placesInputRef.current, {
          fields: ['geometry', 'name', 'formatted_address'],
        });

        listener = autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          const location = place?.geometry?.location;
          if (!location) return;

          const lat = parseFloat(location.lat().toFixed(6));
          const lng = parseFloat(location.lng().toFixed(6));

          setPlacesQuery(place?.formatted_address || place?.name || '');
          setForm((prev) => ({ ...prev, latitude: lat, longitude: lng }));
          setSnackbar({ open: true, message: `Location selected: ${lat}, ${lng}`, severity: 'success' });
        });
      })
      .catch(() => {
        if (!cancelled) {
          setSnackbar({ open: true, message: 'Failed to load Google Places search', severity: 'error' });
        }
      });

    return () => {
      cancelled = true;
      try {
        listener?.remove?.();
      } catch {
        // ignore
      }
    };
  }, [dialogOpen, coordinateMethod, googleMapsApiKey]);

  const fetchPlaceSearchResults = async (query: string) => {
    if (!query.trim()) {
      setPlaceSearchResults([]);
      return;
    }

    setIsPlaceSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query
        )}&addressdetails=1&limit=5`
      );
      if (!response.ok) throw new Error('Search failed');

      const result = await response.json();
      setPlaceSearchResults(Array.isArray(result) ? result : []);
      if (!result?.length) {
        setSnackbar({ open: true, message: 'No locations found', severity: 'error' });
      }
    } catch {
      setPlaceSearchResults([]);
      setSnackbar({ open: true, message: 'Location search failed', severity: 'error' });
    } finally {
      setIsPlaceSearching(false);
    }
  };

  const handlePlaceSearch = async () => {
    if (!placesQuery.trim()) return;
    if (googleMapsApiKey && window.google?.maps?.places) {
      // Google autocomplete will handle results when available.
      return;
    }
    await fetchPlaceSearchResults(placesQuery);
  };

  const handlePlaceSelect = (place: { display_name: string; lat: string; lon: string }) => {
    const lat = parseFloat(place.lat);
    const lon = parseFloat(place.lon);
    setForm((prev) => ({ ...prev, latitude: lat, longitude: lon }));
    setPlacesQuery(place.display_name);
    setPlaceSearchResults([]);
    setSnackbar({ open: true, message: `Location selected: ${lat.toFixed(6)}, ${lon.toFixed(6)}`, severity: 'success' });
  };

  const handleSearch = () => {
    if (page !== 0) {
      setPage(0);
      return;
    }

    dispatch(fetchLocations({
      searchTerm: searchTerm.replace(/^\s+/, '') || undefined,
      pageNumber: 1,
      pageSize: rowsPerPage === -1 ? ALL_ROWS_PAGE_SIZE : rowsPerPage,
    }));
  };

  const handleOpenAdd = () => {
    setEditingLocation(null);
    setForm(emptyForm);
    setFormIsActive(true);
    setCoordinateMethod('manual');
    setPlacesQuery('');
    setDialogOpen(true);
  };

  const handleOpenEdit = (loc: LocationDto) => {
    setEditingLocation(loc);
    setForm({
      locationName: loc.locationName,
      address: loc.address,
      city: loc.city,
      state: loc.state,
      country: loc.country,
      pincode: loc.pincode,
      latitude: loc.latitude,
      longitude: loc.longitude,
      radiusInMeters: loc.radiusInMeters,
      locationCategoryId: loc.locationCategoryId || undefined,
      description: loc.description || '',
    });
    setFormIsActive(loc.isActive);
    setCoordinateMethod('manual');
    setPlacesQuery('');
    setDialogOpen(true);
  };

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setForm((prev) => ({
            ...prev,
            latitude: parseFloat(position.coords.latitude.toFixed(6)),
            longitude: parseFloat(position.coords.longitude.toFixed(6)),
          }));
          setSnackbar({ open: true, message: 'Current location captured', severity: 'success' });
        },
        (error) => {
          setSnackbar({ open: true, message: 'Failed to get location: ' + error.message, severity: 'error' });
        },
        { enableHighAccuracy: true }
      );
    } else {
      setSnackbar({ open: true, message: 'Geolocation not supported by browser', severity: 'error' });
    }
  };



const handleSave = async () => {
  // ✅ block empty or spaces-only
  if (isEmpty(trimmedName)) {
    setSnackbar({
      open: true,
      message: 'Location name cannot be empty',
      severity: 'error',
    });
    return;
  }

  // ✅ Validate first
  const radius = form.radiusInMeters;

  if (!radius) {
    setErrors({ radiusInMeters: "Radius is required" });
    return;
  }

  if (radius < 10 || radius > 10000) {
    setErrors({
      radiusInMeters: "Radius must be between 10 and 10000 meters",
    });
    return;
  }

  try {
    setErrors({}); // ✅ clear old errors

    if (editingLocation) {
      const updateData: UpdateLocation = {
        ...form,
        locationName: trimmedName,
        id: editingLocation.id,
        isActive: formIsActive,
      };

      await dispatch(
        updateLocation({ id: editingLocation.id, data: updateData })
      ).unwrap();

      setSnackbar({
        open: true,
        message: 'Location updated successfully',
        severity: 'success',
      });
      toast.success('Location updated successfully');
    } else {
      await dispatch(createLocation({
        ...form,
        locationName: form.locationName.trim(),
      })).unwrap();

      setSnackbar({
        open: true,
        message: 'Location created successfully',
        severity: 'success',
      });
      toast.success('Location created successfully');
    }

    setDialogOpen(false);

    if (page !== 0) {
      setPage(0);
    } else {
      dispatch(
        fetchLocations({
          searchTerm: searchTerm.replace(/^\s+/, '') || undefined,
          pageNumber: page + 1,
          pageSize:
            rowsPerPage === -1 ? ALL_ROWS_PAGE_SIZE : rowsPerPage,
        })
      );
    }
  } catch (err: any) {
    const message = typeof err === 'string' ? err : 'Operation failed';
    setSnackbar({ open: true, message, severity: 'error' });
    toast.error(message);
  }
};

  const handleDelete = async (id: string) => {
    const locationToDelete = locations.find((location) => location.id === id);

    if (locationToDelete && locationToDelete.whitelistedEmployeeCount > 0) {
      const whitelistEntryCount = locationToDelete.whitelistedEmployeeCount;
      await Swal.fire({
        title: 'Cannot Delete Location',
        text: `"${locationToDelete.locationName}" cannot be deleted because it has ${whitelistEntryCount} whitelist entr${whitelistEntryCount === 1 ? 'y' : 'ies'}. Remove those whitelist assignments first.`,
        icon: 'error',
        confirmButtonColor: '#d33',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Delete Location',
      text: `Are you sure you want to delete "${locationToDelete?.locationName || 'this location'}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete',
    });
    if (result.isConfirmed) {
      try {
        await dispatch(deleteLocation(id)).unwrap();
        setSnackbar({ open: true, message: 'Location deleted', severity: 'success' });
        toast.success('Location deleted successfully');
        if (locations.length === 1 && page > 0) {
          setPage((prev) => prev - 1);
        } else {
          dispatch(fetchLocations({
            searchTerm: searchTerm.replace(/^\s+/, '') || undefined,
            pageNumber: page + 1,
            pageSize: rowsPerPage === -1 ? ALL_ROWS_PAGE_SIZE : rowsPerPage,
          }));
        }
      } catch (err: any) {
        const message = typeof err === 'string' ? err : 'Delete failed';
        setSnackbar({ open: true, message, severity: 'error' });
        toast.error(message);
      }
    }
  };

  const formatCoords = (lat: number, lng: number) => {
    if (lat === 0 && lng === 0) return '-';
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          <LocationIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          GPS Location Management
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd} sx={{ borderRadius: 2 ,textTransform: 'none' }}>
          Add New Location
        </Button>
      </Box>

      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <TextField
          placeholder="Search locations by name, city, or address..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          onKeyUp={(e) => {
              if (e.key !== 'Enter') handleSearch();
            }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
          }}
          sx={{ width: 400 }}
        />
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Location Name</strong></TableCell>
                  <TableCell><strong>Address</strong></TableCell>
                  <TableCell><strong>Coordinates</strong></TableCell>
                  <TableCell><strong>Radius</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Whitelisted</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No locations found. Add your first GPS location.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  locations.map((loc) => (
                    <TableRow key={loc.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon color="primary" fontSize="small" />
                          <Typography variant="body2" fontWeight={500}>{loc.locationName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{loc.address || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[loc.city, loc.state, loc.country].filter(Boolean).join(', ')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontSize={12}>
                          {formatCoords(loc.latitude, loc.longitude)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={`${loc.radiusInMeters}m`} size="small" variant="outlined" color="info" />
                      </TableCell>
                      <TableCell>
                        {loc.locationCategoryName ? (
                          <Chip label={loc.locationCategoryName} size="small" variant="outlined" />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Whitelisted employees">
                          <Chip
                            icon={<PeopleIcon />}
                            label={loc.whitelistedEmployeeCount}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={loc.isActive ? 'Active' : 'Inactive'}
                          color={loc.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary" onClick={() => handleOpenEdit(loc)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(loc.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={locationTotalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />
          </>
        )}
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingLocation ? 'Edit GPS Location' : 'Create New GPS Location'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>

            {/* Location Name */}
            <TextField
              label="Location Name"
              required
              fullWidth
              value={form.locationName}
              onChange={(e) => {
                setForm({ ...form, locationName: e.target.value });
              }}
              placeholder="e.g. Main Office, Branch - Hyderabad"
            />

              {/* Description */}
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={form.description || ''}
                onChange={(e) => {
                  const value = e.target.value;

                  if (value.length <= 200) {
                    setForm({ ...form, description: value });
                  }
                }}
                placeholder="Optional description for this location"
                error={(form.description || '').length > 200}
                helperText={
                  (form.description || '').length > 200
                    ? "Maximum 200 characters allowed"
                    : `${(form.description || '').length}/200`
                }
              />

            {/* Address */}
            <TextField
              label="Address"
              fullWidth
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />

            {/* City / State */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="City" fullWidth value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })} />
              <TextField label="State" fullWidth value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </Box>

            {/* Country / Pincode */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Country" fullWidth value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })} />             
               <TextField
                  label="Pincode"
                  fullWidth
                  value={form.pincode}
                  inputProps={{ maxLength: 6 }}
                  error={form.pincode.length > 0 && form.pincode.length < 6}
                  helperText={
                    form.pincode.length > 0 && form.pincode.length < 6
                      ? "Pincode must be 6 digits"
                      : ""
                  }
                  onChange={(e) => {
                    const value = e.target.value;

                    if (/^\d*$/.test(value)) {
                      setForm({ ...form, pincode: value });
                    }
                  }}
                />
            </Box>

            {/* Coordinate Method Selection */}
            <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Select Coordinate Method
                </FormLabel>
                <RadioGroup
                  row
                  value={coordinateMethod}
                  onChange={(e) => setCoordinateMethod(e.target.value as 'manual' | 'gps' | 'map')}
                >
                  <FormControlLabel value="manual" control={<Radio />} label="Enter coordinates manually" />
                  <FormControlLabel value="gps" control={<Radio />} label="Pick from current GPS location" />
                  <FormControlLabel value="map" control={<Radio />} label="Pick from map" />
                </RadioGroup>
              </FormControl>

              {coordinateMethod === 'manual' && (
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                 <TextField
                  label="Latitude"
                  type="number"
                  fullWidth
                  value={form.latitude}
                  onChange={(e) => {
                    const value = e.target.value;

                    // 🔥 allow only valid decimal format
                    if (!/^-?\d*\.?\d*$/.test(value)) return;

                    // 🔥 limit to 6 decimal places
                    const parts = value.split('.');
                    if (parts[1]?.length > 6) return;

                    const num = parseFloat(value);

                    if (value === '' || (num >= -90 && num <= 90)) {
                      setForm({ ...form, latitude: value === '' ? 0 : num });
                    }
                  }}
                  placeholder="e.g. 17.385044"
                  inputProps={{ step: 'any', min: -90, max: 90 }}
                  error={form.latitude !== 0 && (form.latitude < -90 || form.latitude > 90)}
                  helperText={
                    form.latitude !== 0 && (form.latitude < -90 || form.latitude > 90)
                      ? "Latitude must be between -90 and 90 (max 6 decimal places)"
                      : "Range: -90 to 90 (max 6 decimal places)"
                  }
                />
                                  <TextField
                  label="Longitude"
                  type="number"
                  fullWidth
                  value={form.longitude}
                  onChange={(e) => {
                    const value = e.target.value;

                    // 🔥 allow only valid decimal format
                    if (!/^-?\d*\.?\d*$/.test(value)) return;

                    // 🔥 limit digits before decimal (max 3)
                    const parts = value.split('.');
                    const integerPart = parts[0].replace('-', '');
                    if (integerPart.length > 3) return;

                    // 🔥 limit to 6 decimal places
                    if (parts[1]?.length > 6) return;

                    const num = parseFloat(value);

                    if (value === '' || (num >= -180 && num <= 180)) {
                      setForm({ ...form, longitude: value === '' ? 0 : num });
                    }
                  }}
                  placeholder="e.g. 78.486671"
                  inputProps={{ step: 'any', min: -180, max: 180 }}
                  error={form.longitude !== 0 && (form.longitude < -180 || form.longitude > 180)}
                  helperText={
                    form.longitude !== 0 && (form.longitude < -180 || form.longitude > 180)
                      ? "Longitude must be between -180 and 180 (max 3 digits before & 6 after decimal)"
                      : "Range: -180 to 180 (max 3 digits before & 6 after decimal)"
                  }
                />
                </Box>
              )}

              {coordinateMethod === 'gps' && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<MyLocationIcon />}
                    onClick={handleGetCurrentLocation}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Capture Current GPS Location
                  </Button>
                  {(form.latitude !== 0 || form.longitude !== 0) && (
                    <Alert severity="info">
                      Captured: Lat <strong>{form.latitude.toFixed(6)}</strong>, Lng <strong>{form.longitude.toFixed(6)}</strong>
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Latitude"
                      type="number"
                      fullWidth
                      value={form.latitude}
                      onChange={(e) => setForm({ ...form, latitude: parseFloat(e.target.value) || 0 })}
                      inputProps={{ step: 'any' }}
                    />
                    <TextField
                      label="Longitude"
                      type="number"
                      fullWidth
                      value={form.longitude}
                      onChange={(e) => setForm({ ...form, longitude: parseFloat(e.target.value) || 0 })}
                      inputProps={{ step: 'any' }}
                    />
                  </Box>
                </Box>
              )}

              {coordinateMethod === 'map' && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Search for a location or click on the map to select GPS coordinates. You can also drag the map and zoom in/out.
                  </Alert>
                  <TextField
                    label="Search Location"
                    placeholder="Enter address, city, or place name..."
                    fullWidth
                    value={placesQuery}
                    onChange={(e) => setPlacesQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void handlePlaceSearch();
                      }
                    }}
                    inputRef={placesInputRef}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                      endAdornment: isPlaceSearching ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : undefined,
                    }}
                  />
                  {placeSearchResults.length > 0 && (
                    <Paper variant="outlined" sx={{ mt: 1, maxHeight: 220, overflowY: 'auto' }}>
                      {placeSearchResults.map((result, index) => (
                        <Box
                          key={`${result.lat}-${result.lon}-${index}`}
                          sx={{
                            p: 1,
                            borderBottom: index < placeSearchResults.length - 1 ? '1px solid rgba(0,0,0,0.08)' : 'none',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'grey.100' },
                          }}
                          onClick={() => handlePlaceSelect(result)}
                        >
                          <Typography variant="body2">{result.display_name}</Typography>
                        </Box>
                      ))}
                    </Paper>
                  )}
                  <Box sx={{ height: 350, borderRadius: 2, overflow: 'hidden', border: '1px solid #ccc' }}>
                    {mapUnavailable ? (
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.50',
                          p: 3,
                        }}
                      >
                        <Alert
                          severity="error"
                          action={
                            <Button color="inherit" size="small" onClick={() => setMapUnavailable(!navigator.onLine)}>
                              Retry
                            </Button>
                          }
                        >
                          {MAP_UNAVAILABLE_MESSAGE}
                        </Alert>
                      </Box>
                    ) : (
                      <MapContainer
                        center={[
                          form.latitude !== 0 ? form.latitude : 20.5937,
                          form.longitude !== 0 ? form.longitude : 78.9629,
                        ]}
                        zoom={form.latitude !== 0 ? 15 : 5}
                        style={{ height: '100%', width: '100%' }}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          eventHandlers={{
                            tileerror: () => setMapUnavailable(true),
                            load: () => setMapUnavailable(false),
                          }}
                        />
                        <MapClickHandler
                          onMapClick={(lat, lng) => {
                            setForm((prev) => ({ ...prev, latitude: lat, longitude: lng }));
                            setSnackbar({ open: true, message: `Location selected: ${lat}, ${lng}`, severity: 'success' });
                          }}
                        />
                        <MapRecenter lat={form.latitude} lng={form.longitude} />
                        {(form.latitude !== 0 || form.longitude !== 0) && (
                          <Marker position={[form.latitude, form.longitude]} />
                        )}
                      </MapContainer>
                    )}
                  </Box>
                  {(form.latitude !== 0 || form.longitude !== 0) && (
                    <Alert severity="success">
                      Selected: Lat <strong>{form.latitude.toFixed(6)}</strong>, Lng <strong>{form.longitude.toFixed(6)}</strong>
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Latitude"
                      type="number"
                      fullWidth
                      value={form.latitude}
                      onChange={(e) => setForm({ ...form, latitude: parseFloat(e.target.value) || 0 })}
                      inputProps={{ step: 'any', min: -90, max: 90 }}
                    />
                    <TextField
                      label="Longitude"
                      type="number"
                      fullWidth
                      value={form.longitude}
                      onChange={(e) => setForm({ ...form, longitude: parseFloat(e.target.value) || 0 })}
                      inputProps={{ step: 'any', min: -180, max: 180 }}
                    />
                  </Box>
                </Box>
              )}
            </Paper>

            {/* Geo-Fencing Radius & Category */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Geo-Fencing Radius (meters)"
                type="number"
                fullWidth
                value={form.radiusInMeters || ''}
                onChange={(e) => {
                  const value = e.target.value;

                  if (!/^\d*$/.test(value)) return;

                  setForm({
                    ...form,
                    radiusInMeters: value === '' ? 0 : parseInt(value),
                  });

                  // ✅ clear error while typing
                  if (errors.radiusInMeters) {
                    setErrors({ ...errors, radiusInMeters: undefined });
                  }
                }}
                helperText={
                  form.radiusInMeters !== 0 &&
                  (form.radiusInMeters < 10 || form.radiusInMeters > 10000)
                    ? "Radius must be between 10 and 10000 meters"
                    : "Defines the allowed attendance check-in area"
                }
                error={
                  form.radiusInMeters !== 0 &&
                  (form.radiusInMeters < 10 || form.radiusInMeters > 10000)
                }
              />
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={form.locationCategoryId || ''}
                  label="Category"
                  onChange={(e) => setForm({ ...form, locationCategoryId: (e.target.value as string) || undefined })}
                >
                  <MenuItem value="">None</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.categoryName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Active toggle for edit */}
            {editingLocation && (
              <FormControlLabel
                control={<Switch checked={formIsActive} onChange={(e) => setFormIsActive(e.target.checked)} />}
                label="Location Active"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              isEmpty(form.locationName) || // ✅ FIXED
              (form.latitude === 0 && form.longitude === 0)
            }
              sx={{ textTransform: 'none' }}
          >
            {editingLocation ? 'Update Location' : 'Create Location'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LocationList;
